import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

const COLORS = ["#0F172A", "#D97706", "#16A34A", "#DC2626", "#0EA5E9", "#7C3AED", "#EAB308", "#EC4899"];
const CHART_BOX_STYLE = { width: "100%", height: 340 };
const AXIS_TICK_STYLE = { fontSize: 11 };
const LEGEND_STYLE = { fontSize: 11 };

function usePropertyStats(api, refreshKey) {
  const [stats, setStats] = useState(null);
  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${api}/stats`);
      setStats(res.data);
    } catch (e) { toast.error("Failed to load stats"); }
  }, [api]);
  useEffect(() => { load(); }, [refreshKey, load]);
  return stats;
}

function Kpi({ label, value, tone }) {
  return (
    <div className={`rounded-lg border p-4 ${tone || "bg-white border-slate-200"}`}>
      <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-3xl font-semibold tracking-tight mt-1 text-slate-900">{value}</div>
    </div>
  );
}

function KpiRow({ stats }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="kpi-row">
      <Kpi label="Total" value={stats.total} />
      <Kpi label="Open" value={stats.open} tone="bg-amber-50 border-amber-200" />
      <Kpi label="Completed" value={stats.completed} tone="bg-emerald-50 border-emerald-200" />
      <Kpi label="Overdue" value={stats.overdue} tone="bg-red-50 border-red-200" />
      <Kpi label="Due Today" value={stats.due_today} tone="bg-slate-900 border-slate-900 text-white" />
    </div>
  );
}

function PropertyBarChart({ data }) {
  return (
    <Card className="lg:col-span-2 border-slate-200 shadow-sm">
      <CardHeader className="border-b border-slate-100">
        <div className="text-[11px] font-mono uppercase tracking-widest text-amber-600 mb-1">By Property</div>
        <CardTitle className="text-lg">Work Orders by Location</CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <div style={CHART_BOX_STYLE} data-testid="property-chart">
          <ResponsiveContainer>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={AXIS_TICK_STYLE} />
              <YAxis allowDecimals={false} tick={AXIS_TICK_STYLE} />
              <Tooltip />
              <Legend />
              <Bar dataKey="Outstanding" fill="#D97706" />
              <Bar dataKey="Completed" fill="#16A34A" />
              <Bar dataKey="Overdue" fill="#DC2626" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function IssuePieChart({ data }) {
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="border-b border-slate-100">
        <div className="text-[11px] font-mono uppercase tracking-widest text-amber-600 mb-1">By Issue Type</div>
        <CardTitle className="text-lg">Category Distribution</CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <div style={CHART_BOX_STYLE} data-testid="issue-chart">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" innerRadius={55} outerRadius={100} paddingAngle={2}>
                {data.map((entry, i) => <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={LEGEND_STYLE} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function PropertyTable({ byProperty }) {
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="border-b border-slate-100">
        <div className="text-[11px] font-mono uppercase tracking-widest text-amber-600 mb-1">Detail</div>
        <CardTitle className="text-lg">Per-Property Breakdown</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Property</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-right px-4 py-3">New</th>
              <th className="text-right px-4 py-3">In Progress</th>
              <th className="text-right px-4 py-3">Completed</th>
              <th className="text-right px-4 py-3">Overdue</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(byProperty).map(([name, v]) => (
              <tr key={name} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`stats-row-${name.replace(/\s+/g, "-")}`}>
                <td className="px-4 py-3 font-medium">{name}</td>
                <td className="px-4 py-3 text-right">{v.total || 0}</td>
                <td className="px-4 py-3 text-right">{v.new || 0}</td>
                <td className="px-4 py-3 text-right">{v.in_progress || 0}</td>
                <td className="px-4 py-3 text-right text-emerald-700">{v.completed || 0}</td>
                <td className="px-4 py-3 text-right text-red-600 font-semibold">{v.overdue || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

export default function PropertyStats({ api, refreshKey }) {
  const stats = usePropertyStats(api, refreshKey);

  if (!stats) return <div className="text-sm text-slate-500">Loading stats...</div>;

  const propertyData = Object.entries(stats.by_property).map(([name, v]) => ({
    name,
    Outstanding: (v.new || 0) + (v.in_progress || 0),
    Completed: v.completed || 0,
    Overdue: v.overdue || 0,
  }));
  const issueData = Object.entries(stats.by_issue).map(([name, value]) => ({ name, value }));

  return (
    <div className="space-y-6">
      <KpiRow stats={stats} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <PropertyBarChart data={propertyData} />
        <IssuePieChart data={issueData} />
      </div>
      <PropertyTable byProperty={stats.by_property} />
    </div>
  );
}
